import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "../components/Seo";
import { toast } from "sonner";
import { getConfig, riderRegister } from "../lib/api";

export default function RiderRegister() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", password: "", sectors: [] });
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { getConfig().then(setConfig).catch(() => {}); }, []);

  const toggleSector = (s) => {
    setForm((f) => ({
      ...f,
      sectors: f.sectors.includes(s) ? f.sectors.filter((x) => x !== s) : [...f.sectors, s],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.password) {
      toast.error("Please fill all fields");
      return;
    }
    setLoading(true);
    try {
      const r = await riderRegister({
        name: form.name.trim(),
        phone: form.phone.trim(),
        password: form.password,
        sectors: form.sectors,
      });
      localStorage.setItem("rw_rider_token", r.token);
      toast.success(`Welcome ${r.rider.name} · Rider ID ${r.rider.rider_code}`);
      navigate("/rider", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not sign up");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-rw-bg text-rw-ink" data-testid="rider-register-page">
      <Seo title="Rider Sign-up" description="Join RIZ N WASH as a delivery rider." path="/rider/register" noindex={true} />
      <div className="max-w-md mx-auto px-6 md:px-10 pt-14">
        <div className="overline mb-3">Rider sign-up</div>
        <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight leading-[1.05]">
          Ride with us.
        </h1>
        <p className="mt-3 text-rw-ink2">Get started in under a minute. You&apos;ll receive a Rider ID after signing up.</p>

        <form onSubmit={submit} className="mt-8 rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-6 space-y-3" data-testid="rider-register-form">
          <label className="block">
            <span className="overline">Name</span>
            <input
              className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2.5 rw-focus"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ramesh Kumar"
              data-testid="rider-reg-name"
            />
          </label>
          <label className="block">
            <span className="overline">Phone (WhatsApp)</span>
            <input
              className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2.5 rw-focus"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+91 98xxx xxxxx"
              data-testid="rider-reg-phone"
            />
          </label>
          <label className="block">
            <span className="overline">Password</span>
            <input
              type="password"
              className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2.5 rw-focus"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="At least 4 characters"
              data-testid="rider-reg-password"
            />
          </label>
          <div>
            <span className="overline">Sectors you cover</span>
            <div className="mt-2 flex flex-wrap gap-2">
              {(config?.sectors || []).map((s) => (
                <button
                  type="button"
                  key={s}
                  onClick={() => toggleSector(s)}
                  className={`text-xs rounded-full px-3 py-1.5 border ${
                    form.sectors.includes(s)
                      ? "bg-rw-ink text-white border-rw-ink"
                      : "border-rw-ink/20 text-rw-ink2 hover:bg-rw-bg3"
                  }`}
                  data-testid={`rider-reg-sector-${s.replace(/\s+/g, "-")}`}
                >
                  {s}
                </button>
              ))}
            </div>
            <p className="text-xs text-rw-ink3 mt-2">Pick any that apply — you can change later. Empty means citywide.</p>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-dark w-full rounded-full py-2.5 text-sm font-semibold disabled:opacity-40"
            data-testid="rider-reg-submit"
          >
            {loading ? "Creating account…" : "Create rider account"}
          </button>
        </form>

        <p className="mt-6 text-sm text-rw-ink2 text-center">
          Already have an account? <Link to="/rider" className="text-rw-ink font-semibold hover:underline" data-testid="rider-link-login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
